import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Client } from '@langchain/langgraph-sdk'
import { supabase } from '@/lib/supabase'

export interface AuthenticatedClient {
  readonly client: Client
  readonly isReady: boolean
}

export function useAuthenticatedClient(apiUrl: string): AuthenticatedClient {
  const tokenRef = useRef<string | null>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      tokenRef.current = session?.access_token ?? null
      if (session?.access_token) setIsReady(true)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null
      setIsReady(session?.access_token != null)
    })

    return (): void => { subscription.unsubscribe() }
  }, [])

  const onRequest = useCallback((_url: URL, init: RequestInit): RequestInit => {
    const token = tokenRef.current
    if (!token) return init
    const headers = new Headers(init.headers)
    headers.set('Authorization', `Bearer ${token}`)
    return { ...init, headers }
  }, [])

  const client = useMemo(
    () => new Client({ apiUrl, onRequest }),
    [apiUrl, onRequest],
  )

  return { client, isReady }
}
